/**
 * Fiches de préparation d'un jour de livraison — une par commande.
 *
 * Partagé entre le tableau de bord (bureau) et la caisse (comptoir) : la
 * préparation se fait au POS, il ne doit pas falloir passer par le dashboard
 * pour sortir la fiche.
 *
 * Ne fait aucun appel réseau : l'appelant fournit le résultat de
 * GET /preorders/preparation-sheets, ce qui laisse chaque écran choisir sa
 * façon de sélectionner le jour et la famille.
 */

interface SheetLine {
  product_name: string;
  quantity: number;
  unit?: string | null;
  prep_options?: string[];
  note?: string | null;
}

export interface PreparationSheet {
  preorder_id: string;
  preorder_number: string;
  customer_name?: string;
  customer_phone?: string;
  pickup_date: string;
  set_aside: boolean;
  notes?: string | null;
  lines: SheetLine[];
  already_in_reserve?: SheetLine[];
  still_to_come?: SheetLine[];
}

export interface PreparationSheetsData {
  deliveryDate: string;
  sheets: PreparationSheet[];
  totalSheets: number;
  totalItems: number;
}

const longDate = (d: string) =>
  new Date(d).toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long' });

export default function PreparationSheetsPanel({
  data,
  onClose,
}: {
  data: PreparationSheetsData;
  onClose: () => void;
}) {
  if (!data?.sheets?.length) return null;

  return (
    <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4 sm:p-6 mb-6 print:bg-white print:border-0">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="text-lg sm:text-xl font-bold text-amber-900">
          🧾 Préparation du {longDate(data.deliveryDate)}
        </h2>
        <div className="flex gap-2 print:hidden">
          <button
            onClick={() => window.print()}
            className="px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-sm font-medium min-h-[48px]"
          >
            Imprimer
          </button>
          <button
            onClick={onClose}
            className="px-3 py-2 bg-white border border-amber-300 text-amber-800 rounded-md text-sm min-h-[48px]"
          >
            Fermer
          </button>
        </div>
      </div>

      <p className="text-sm text-amber-800 mb-4">
        {data.totalSheets} commande{data.totalSheets > 1 ? 's' : ''} ·{' '}
        {data.totalItems} article{data.totalItems > 1 ? 's' : ''} à préparer
      </p>

      <div className="space-y-4">
        {data.sheets.map((sheet) => (
          <div
            key={sheet.preorder_id}
            className="bg-white border border-amber-200 rounded-lg p-4 break-inside-avoid"
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0">
                <p className="font-bold text-gray-900 truncate">
                  {sheet.customer_name || 'Client'}
                  <span className="font-normal text-gray-500"> · {sheet.preorder_number}</span>
                </p>
                {sheet.customer_phone && (
                  <p className="text-xs text-gray-500">{sheet.customer_phone}</p>
                )}
              </div>
              {/* La consigne qui manquait : ce qui est livré aujourd'hui mais
                  retiré plus tard doit être mis de côté, pas donné. */}
              {sheet.set_aside ? (
                <span className="shrink-0 text-xs font-bold px-2 py-1 rounded bg-amber-200 text-amber-900">
                  À METTRE DE CÔTÉ — retrait {longDate(sheet.pickup_date)}
                </span>
              ) : (
                <span className="shrink-0 text-xs font-bold px-2 py-1 rounded bg-green-100 text-green-800">
                  RETRAIT AUJOURD’HUI
                </span>
              )}
            </div>

            <ul className="mt-2 space-y-1">
              {sheet.lines.map((l, idx) => (
                <li key={idx} className="flex justify-between text-sm">
                  <span>
                    {l.product_name}
                    {(l.prep_options?.length ?? 0) > 0 && (
                      <span className="text-gray-500"> ({l.prep_options!.join(', ')})</span>
                    )}
                    {l.note && <span className="text-gray-500 italic"> — {l.note}</span>}
                  </span>
                  <span className="font-semibold ml-3 shrink-0">
                    {l.quantity} {l.unit || ''}
                  </span>
                </li>
              ))}
            </ul>

            {(sheet.already_in_reserve?.length ?? 0) > 0 && (
              <p className="mt-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded p-2">
                ➕ Déjà en réserve, à joindre :{' '}
                {sheet.already_in_reserve!.map((l) => `${l.product_name} ×${l.quantity}`).join(', ')}
              </p>
            )}
            {(sheet.still_to_come?.length ?? 0) > 0 && (
              <p className="mt-2 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded p-2">
                ⏳ Arrive plus tard :{' '}
                {sheet.still_to_come!.map((l) => `${l.product_name} ×${l.quantity}`).join(', ')}
              </p>
            )}
            {sheet.notes && (
              <p className="mt-2 text-xs text-gray-600 italic">Note : {sheet.notes}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
